import { create } from 'zustand';
import type { Person } from '../types';

/** #1825 deel 2 — de rol van de nieuwe persoon t.o.v. `anderePersoonId` ("Deze persoon is … van …"). */
export type PlekRol = 'father' | 'mother' | 'partner' | 'child' | 'sibling';
/** #1825 deel 2 — voorkeuze voor "Plek in de stamboom"; `'los'` = "Nog niet gekoppeld". */
export type PlekOptie = { rol: PlekRol; anderePersoonId: string } | 'los';

export interface OpenEditorOptions {
  gender?: 'M' | 'F' | 'U';
  initialTab?: string;
  initialSelectedEventId?: string;
  /** #1597 — open deze sectie meteen, ook als hij leeg is (rapportbouwer: levensverhaal). */
  initialSection?: 'reportText';
  /** #1825 deel 2 — alleen bij een nieuwe persoon: de plek-keuze staat al ingevuld. */
  plek?: PlekOptie;
  /**
   * #1825 deel 3 — alleen bij een nieuwe persoon: wat de gebruiker elders al typte (telefoon,
   * "Meer gegevens"). Telt als wijziging: sluiten stelt de vraag over niet-opgeslagen wijzigingen.
   */
  voorinvulling?: Partial<Person>;
  /**
   * #1825 eindreview — alleen bij een nieuwe persoon: het id van een eerdere opslagpoging met
   * onbekende afloop (telefoon, "Meer gegevens" na een time-out). Zo maakt het formulier geen
   * tweede persoon als die poging toch slaagde.
   */
  clientId?: string;
}

interface EditorState {
  // State
  isOpen: boolean;
  person: Person | null;
  initialGender: 'M' | 'F' | 'U' | undefined;
  initialTab?: string;
  initialSelectedEventId?: string;
  initialSection?: 'reportText';
  initialPlek?: PlekOptie;
  initialVoorinvulling?: Partial<Person>;
  initialClientId?: string;
  /** #1825 — telt per `openEditor` op; AppModals remount de editor hierop (`key`). */
  sessionId: number;

  // Actions
  openEditor: (
    person: Person | null,
    genderOrOptions?: 'M' | 'F' | 'U' | OpenEditorOptions
  ) => void;
  /**
   * #1825 — de nieuwe persoon in de open editor is opgeslagen: de store kent
   * vanaf nu zijn id, maar het blijft dezelfde editorsessie (geen remount).
   */
  markEditingPersonSaved: (person: Person) => void;
  closeEditor: () => void;
}

export const useEditorStore = create<EditorState>((set) => ({
  // Initial state
  isOpen: false,
  person: null,
  initialGender: undefined,
  initialTab: undefined,
  initialSelectedEventId: undefined,
  initialSection: undefined,
  initialPlek: undefined,
  initialVoorinvulling: undefined,
  initialClientId: undefined,
  sessionId: 0,

  // Actions
  // #1825 — elke aanroep is een nieuwe editorsessie: AppModals remount de editor
  // (`key={sessionId}`) en niet-opgeslagen wijzigingen gaan verloren. Wie kan
  // heropenen terwijl de editor open is, loopt eerst via de dirty-vraag
  // (PersonEditor `requestCloseRef`).
  openEditor: (person, genderOrOptions) => {
    // Back-compat: string = gender, object = volledige opties
    const opts: OpenEditorOptions =
      typeof genderOrOptions === 'string' ? { gender: genderOrOptions } : genderOrOptions || {};
    set((s) => ({
      sessionId: s.sessionId + 1,
      isOpen: true,
      person,
      initialGender: opts.gender,
      initialTab: opts.initialTab,
      initialSelectedEventId: opts.initialSelectedEventId,
      initialSection: opts.initialSection,
      initialPlek: opts.plek,
      initialVoorinvulling: opts.voorinvulling,
      initialClientId: opts.clientId,
    }));
  },

  markEditingPersonSaved: (person) => set((s) => (s.isOpen ? { person } : {})),

  closeEditor: () =>
    set({
      isOpen: false,
      person: null,
      initialGender: undefined,
      initialTab: undefined,
      initialSelectedEventId: undefined,
      initialSection: undefined,
      initialPlek: undefined,
      initialVoorinvulling: undefined,
      initialClientId: undefined,
    }),
}));
